import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { api, getToken } from '../api/client'
import type { EagleBloccoTipo, EagleReportDef, EagleReportEsito, EagleMonitorato } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'
import { Card, EmptyState, ErrorBanner, Spinner, formatDateTime } from './common'
import { DataTable } from './DataTable'
import { StatoBadge } from './EagleStato'
import { formatValore } from './eagleTesti'
import { GraficoSerie } from './GraficoSerie'
import { GraficoStati } from './GraficoStati'

/**
 * Eagle → Report (ADR-0022): report che si **compongono**.
 *
 * Un report è una definizione — periodo, ambito, blocchi in ordine — che si
 * esegue ogni volta sui dati del momento. L'editor è una lista di blocchi
 * presi da un catalogo, con le loro opzioni, da spostare su e giù; l'anteprima
 * è il report vero. Si scarica in PDF (le tabelle, i grafici diventano numeri)
 * e in Excel.
 */

/** Nomi di formato, uguali in ogni lingua. */
const NOME_PDF = 'PDF'
const NOME_EXCEL = 'Excel'

const BLOCCHI: { tipo: EagleBloccoTipo; label: () => string; descrizione: () => string }[] = [
  { tipo: 'summary', label: () => t('Riepilogo'), descrizione: () => t('CI, disponibilità media, incidenti, tempo giù, SLA rispettati, il peggiore.') },
  { tipo: 'availability', label: () => t('Disponibilità per CI'), descrizione: () => t('Per ogni CI: disponibilità, tempo giù, incidenti, copertura, SLA.') },
  { tipo: 'incidents', label: () => t('Incidenti'), descrizione: () => t('I tratti in cui un CI non rispondeva: da, a, durata.') },
  { tipo: 'alarms_timeline', label: () => t('Parametri in allarme nel tempo'), descrizione: () => t('L’andamento degli allarmi intervallo per intervallo.') },
  { tipo: 'top_params', label: () => t('Parametri più alti'), descrizione: () => t('I parametri di un tipo con la media più alta: CPU, memoria, volumi, interfacce.') },
  { tipo: 'param_chart', label: () => t('Grafico di un parametro'), descrizione: () => t('L’andamento di un parametro di un CI nel periodo.') },
  { tipo: 'sla', label: () => t('Rispetto degli SLA'), descrizione: () => t('Per ogni SLA: quanti CI lo rispettano e quanti no.') },
  { tipo: 'text', label: () => t('Testo'), descrizione: () => t('Un titolo e un testo libero: una premessa, una conclusione.') },
]

const TIPI_TOP: { key: string; label: () => string }[] = [
  { key: 'cpu', label: () => t('CPU') }, { key: 'memory', label: () => t('Memoria') }, { key: 'volume', label: () => t('Volumi') },
  { key: 'interface', label: () => t('Interfacce') }, { key: 'tcp', label: () => t('Porte TCP') }, { key: 'ping', label: () => t('Ping') },
]

const PERIODI: { key: string; periodo: EagleReportDef['period']; label: () => string }[] = [
  { key: '24', periodo: { kind: 'last', hours: 24 }, label: () => t('Ultime 24 ore') },
  { key: '168', periodo: { kind: 'last', hours: 168 }, label: () => t('Ultimi 7 giorni') },
  { key: '720', periodo: { kind: 'last', hours: 720 }, label: () => t('Ultimi 30 giorni') },
  { key: 'm0', periodo: { kind: 'month', offset: 0 }, label: () => t('Questo mese') },
  { key: 'm1', periodo: { kind: 'month', offset: 1 }, label: () => t('Il mese scorso') },
]

function chiavePeriodo(p: EagleReportDef['period']): string {
  return p.kind === 'month' ? `m${p.offset ?? 0}` : String(p.hours ?? 168)
}

const VUOTO: EagleReportDef = {
  name: '', description: '', period: { kind: 'last', hours: 168 }, scope: { mode: 'all' },
  blocks: [{ type: 'summary', options: {} }, { type: 'availability', options: { sort: 'worst', limit: 50 } }, { type: 'incidents', options: { limit: 100 } }],
}

export function EagleReport({ puoModificare }: { puoModificare: boolean }) {
  const elenco = useAsync(() => api.eagleReports(), [])
  const [modo, setModo] = useState<'elenco' | 'editor' | 'vista'>('elenco')
  const [def, setDef] = useState<EagleReportDef>(VUOTO)
  const [idAperto, setIdAperto] = useState<string | null>(null)
  const [esito, setEsito] = useState<EagleReportEsito | null>(null)
  const [errore, setErrore] = useState<unknown>(null)
  const [invio, setInvio] = useState<string | null>(null)

  if (elenco.loading && !elenco.data) return <Spinner />
  if (!elenco.data?.licensed) {
    return <EmptyState title={t('Pagina non disponibile')} hint={t('Questa funzione non è attiva per il tuo spazio.')} />
  }
  const reports = elenco.data.reports

  async function esegui(fn: () => Promise<void>, etichetta: string) {
    setInvio(etichetta)
    setErrore(null)
    try {
      await fn()
    } catch (e) {
      setErrore(e)
    } finally {
      setInvio(null)
    }
  }

  function nuovo() {
    setDef(VUOTO)
    setIdAperto(null)
    setEsito(null)
    setModo('editor')
  }
  function modifica(r: EagleReportDef & { id: string }) {
    setDef({ name: r.name, description: r.description ?? '', period: r.period, scope: r.scope, blocks: r.blocks })
    setIdAperto(r.id)
    setEsito(null)
    setModo('editor')
  }
  async function apri(r: EagleReportDef & { id: string }) {
    await esegui(async () => {
      setEsito(await api.eagleReportEsegui(r.id))
      setIdAperto(r.id)
      setDef({ name: r.name, description: r.description ?? '', period: r.period, scope: r.scope, blocks: r.blocks })
      setModo('vista')
    }, `apri:${r.id}`)
  }
  async function anteprima() {
    await esegui(async () => {
      setEsito(await api.eagleReportAnteprima(def))
    }, 'anteprima')
  }
  async function salva() {
    await esegui(async () => {
      if (idAperto) await api.eagleReportAggiorna(idAperto, def)
      else await api.eagleReportCrea(def)
      await elenco.reload()
      setModo('elenco')
    }, 'salva')
  }
  async function togli(id: string) {
    if (!window.confirm(t('Togliere questo report?'))) return
    await esegui(async () => {
      await api.eagleReportElimina(id)
      await elenco.reload()
    }, `togli:${id}`)
  }
  async function scarica(id: string, fmt: 'pdf' | 'xlsx') {
    await esegui(async () => {
      const resp = await fetch(`/api/v1/monitoring/reports/${id}/run?fmt=${fmt}`, { headers: { Authorization: `Bearer ${getToken() ?? ''}` } })
      if (!resp.ok) throw new Error(t('Esportazione non riuscita (HTTP {status})', { status: resp.status }))
      const blob = await resp.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `censo-eagle-report.${fmt}`
      a.click()
      URL.revokeObjectURL(url)
    }, `scarica:${id}:${fmt}`)
  }

  if (modo === 'editor') {
    return (
      <>
        <ErrorBanner error={errore} />
        <Editor def={def} onCambia={setDef} />
        <p style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="secondary" onClick={anteprima} disabled={invio !== null || !def.name.trim()}>
            {invio === 'anteprima' ? t('Eseguo…') : t('Anteprima')}
          </button>
          {puoModificare && (
            <button type="button" onClick={salva} disabled={invio !== null || !def.name.trim() || def.blocks.length === 0}>
              {invio === 'salva' ? t('Salvo…') : t('Salva')}
            </button>
          )}
          <button type="button" className="secondary" onClick={() => setModo('elenco')} disabled={invio !== null}>{t('Annulla')}</button>
        </p>
        {esito && <Vista esito={esito} />}
      </>
    )
  }

  if (modo === 'vista' && esito) {
    return (
      <>
        <ErrorBanner error={errore} />
        <p style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" className="secondary" onClick={() => setModo('elenco')}>{t('← Report')}</button>
          {idAperto && <>
            <button type="button" className="secondary" onClick={() => void scarica(idAperto, 'pdf')} disabled={invio !== null}>{t('Scarica PDF')}</button>
            <button type="button" className="secondary" onClick={() => void scarica(idAperto, 'xlsx')} disabled={invio !== null}>{t('Scarica Excel')}</button>
          </>}
          {puoModificare && idAperto && (
            <button type="button" className="secondary" onClick={() => setModo('editor')}>{t('Modifica')}</button>
          )}
          <span className="muted eagle-piccolo">{t('generato {quando}', { quando: formatDateTime(esito.generated_at) })}</span>
        </p>
        <Vista esito={esito} />
      </>
    )
  }

  return (
    <>
      <ErrorBanner error={errore} />
      {puoModificare && (
        <p><button type="button" onClick={nuovo}>{t('Nuovo report')}</button></p>
      )}
      {reports.length === 0 ? (
        <EmptyState title={t('Nessun report ancora')}
                    hint={t('Crea il primo report: seleziona il periodo, i CI e i blocchi da includere, come riepilogo, disponibilità, incidenti, grafici e SLA; verifica l’anteprima e salva.')} />
      ) : (
        <Card title={t('Report ({n})', { n: reports.length })}>
          <DataTable
            rows={reports}
            rowKey={(r) => r.id}
            conteggio={{ nome: t('report') }}
            columns={[
              {
                key: 'name', header: t('Nome'), value: (r) => [r.name, r.description].filter(Boolean).join(' '),
                render: (r) => (
                  <>
                    <button type="button" className="linklike" onClick={() => void apri(r)} disabled={invio !== null}><strong>{r.name}</strong></button>
                    {r.description && <div className="muted eagle-piccolo">{r.description}</div>}
                  </>
                ),
              },
              { key: 'period', header: t('Periodo'), value: (r) => PERIODI.find((p) => p.key === chiavePeriodo(r.period))?.label() ?? '' },
              { key: 'scope', header: t('Ambito'), value: (r) => etichettaAmbito(r.scope) },
              { key: 'blocks', header: t('Blocchi'), value: (r) => r.blocks.map((b) => BLOCCHI.find((x) => x.tipo === b.type)?.label() ?? b.type).join(' · ') },
              { key: 'updated_at', header: t('Aggiornato'), type: 'date', render: (r) => formatDateTime(r.updated_at) },
              {
                key: 'actions', header: <span aria-label={t('Azioni')} />, sort: false, filter: false, searchable: false,
                render: (r) => (
                  <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button type="button" className="secondary" onClick={() => void scarica(r.id, 'pdf')} disabled={invio !== null}>{NOME_PDF}</button>
                    <button type="button" className="secondary" onClick={() => void scarica(r.id, 'xlsx')} disabled={invio !== null}>{NOME_EXCEL}</button>
                    {puoModificare && <>
                      <button type="button" className="secondary" onClick={() => modifica(r)} disabled={invio !== null}>{t('Modifica')}</button>
                      <button type="button" className="secondary" onClick={() => void togli(r.id)} disabled={invio !== null}>{t('Togli')}</button>
                    </>}
                  </span>
                ),
              },
            ]}
          />
        </Card>
      )}
    </>
  )
}

function etichettaAmbito(s: EagleReportDef['scope']): string {
  if (s.mode === 'cis') return t('{n} CI scelti', { n: s.ci_ids?.length ?? 0 })
  if (s.mode === 'class') return t('classi: {classi}', { classi: (s.classes ?? []).join(', ') })
  return t('tutti i CI monitorati')
}

// ------------------------------------------------------------------ editor

function Editor({ def, onCambia }: { def: EagleReportDef; onCambia: (d: EagleReportDef) => void }) {
  const cis = useAsync(() => api.eagleCis(), [])
  const [cerca, setCerca] = useState('')
  const elencoCis: EagleMonitorato[] = cis.data?.cis ?? []
  const classi = [...new Set(elencoCis.map((c) => c.ci_class).filter((x): x is string => !!x))].sort()
  const blocco = (i: number, parte: Partial<{ options: Record<string, unknown> }>) =>
    onCambia({ ...def, blocks: def.blocks.map((b, j) => (j === i ? { ...b, options: { ...b.options, ...(parte.options ?? {}) } } : b)) })
  const sposta = (i: number, di: number) => {
    const j = i + di
    if (j < 0 || j >= def.blocks.length) return
    const blocks = [...def.blocks]
    ;[blocks[i], blocks[j]] = [blocks[j], blocks[i]]
    onCambia({ ...def, blocks })
  }
  return (
    <Card title={t('Composizione del report')}>
      <div className="eagle-form" style={{ maxWidth: 'none' }}>
        <div className="eagle-report__riga">
          <label className="field"><span className="field__label">{t('Nome')}</span>
            <input value={def.name} onChange={(e) => onCambia({ ...def, name: e.target.value })} placeholder={t('es. Disponibilità mensile')} /></label>
          <label className="field"><span className="field__label">{t('Descrizione')}</span>
            <input value={def.description ?? ''} onChange={(e) => onCambia({ ...def, description: e.target.value })} /></label>
          <label className="field"><span className="field__label">{t('Periodo')}</span>
            <select value={chiavePeriodo(def.period)} onChange={(e) => onCambia({ ...def, period: PERIODI.find((p) => p.key === e.target.value)!.periodo })}>
              {PERIODI.map((p) => <option key={p.key} value={p.key}>{p.label()}</option>)}
            </select></label>
        </div>
        <fieldset className="field"><legend className="field__label">{t('Ambito')}</legend>
          <div className="segmented" role="tablist" style={{ marginBottom: 8 }}>
            {(['all', 'class', 'cis'] as const).map((m) => (
              <button key={m} type="button" role="tab" aria-selected={def.scope.mode === m} className={def.scope.mode === m ? 'segmented__on' : ''}
                      onClick={() => onCambia({ ...def, scope: { mode: m, ci_ids: def.scope.ci_ids ?? [], classes: def.scope.classes ?? [] } })}>
                {m === 'all' ? t('Tutti i CI monitorati') : m === 'class' ? t('Per classe') : t('CI scelti')}
              </button>
            ))}
          </div>
          {def.scope.mode === 'class' && (
            <div className="eagle-scelta-servizi">
              {classi.map((c) => (
                <label key={c}><input type="checkbox" checked={(def.scope.classes ?? []).includes(c)}
                  onChange={() => { const cur = def.scope.classes ?? []; onCambia({ ...def, scope: { ...def.scope, classes: cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c] } }) }} />{' '}{c}</label>
              ))}
            </div>
          )}
          {def.scope.mode === 'cis' && (
            <>
              <input value={cerca} onChange={(e) => setCerca(e.target.value)} placeholder={t('Cerca un CI')} aria-label={t('Cerca un CI')} style={{ maxWidth: 300 }} />
              <div className="eagle-scelta-servizi">
                {elencoCis.filter((c) => !cerca || c.name.toLowerCase().includes(cerca.toLowerCase())).map((c) => (
                  <label key={c.ci_id}><input type="checkbox" checked={(def.scope.ci_ids ?? []).includes(c.ci_id)}
                    onChange={() => { const cur = def.scope.ci_ids ?? []; onCambia({ ...def, scope: { ...def.scope, ci_ids: cur.includes(c.ci_id) ? cur.filter((x) => x !== c.ci_id) : [...cur, c.ci_id] } }) }} />{' '}{c.name}<span className="muted"> · {c.ci_class ?? ''}</span></label>
                ))}
              </div>
            </>
          )}
        </fieldset>
        <fieldset className="field"><legend className="field__label">{t('Blocchi, nell’ordine del report')}</legend>
          {def.blocks.length === 0 && <p className="muted">{t('Aggiungi almeno un blocco.')}</p>}
          <ol className="eagle-blocchi">
            {def.blocks.map((b, i) => {
              const info = BLOCCHI.find((x) => x.tipo === b.type)
              return (
                <li key={i} className="eagle-blocco">
                  <div className="eagle-blocco__testa">
                    <strong>{info?.label() ?? b.type}</strong>
                    <span className="muted eagle-piccolo">{info?.descrizione()}</span>
                    <span className="eagle-blocco__azioni">
                      <button type="button" className="linklike" onClick={() => sposta(i, -1)} disabled={i === 0} aria-label={t('Sposta su')}>↑</button>
                      <button type="button" className="linklike" onClick={() => sposta(i, 1)} disabled={i === def.blocks.length - 1} aria-label={t('Sposta giù')}>↓</button>
                      <button type="button" className="linklike" onClick={() => onCambia({ ...def, blocks: def.blocks.filter((_, j) => j !== i) })}>{t('togli')}</button>
                    </span>
                  </div>
                  {b.type === 'top_params' && (
                    <div className="eagle-report__riga">
                      <label className="field"><span className="field__label">{t('Tipo di parametro')}</span>
                        <select value={String(b.options.kind ?? 'cpu')} onChange={(e) => blocco(i, { options: { kind: e.target.value } })}>
                          {TIPI_TOP.map((x) => <option key={x.key} value={x.key}>{x.label()}</option>)}
                        </select></label>
                      <label className="field"><span className="field__label">{t('Quanti')}</span>
                        <input type="number" min={1} max={500} value={Number(b.options.limit ?? 10)} onChange={(e) => blocco(i, { options: { limit: Number(e.target.value) } })} style={{ width: 90 }} /></label>
                    </div>
                  )}
                  {(b.type === 'availability' || b.type === 'incidents') && (
                    <label className="field"><span className="field__label">{t('Al massimo (righe)')}</span>
                      <input type="number" min={1} max={500} value={Number(b.options.limit ?? 50)} onChange={(e) => blocco(i, { options: { limit: Number(e.target.value) } })} style={{ width: 90 }} /></label>
                  )}
                  {b.type === 'param_chart' && (
                    <SceltaParametro ciId={String(b.options.ci_id ?? '')} chiave={String(b.options.key ?? '')} cis={elencoCis}
                                     onCambia={(ci_id, key) => blocco(i, { options: { ci_id, key } })} />
                  )}
                  {b.type === 'text' && (
                    <>
                      <label className="field"><span className="field__label">{t('Titolo')}</span>
                        <input value={String(b.options.title ?? '')} onChange={(e) => blocco(i, { options: { title: e.target.value } })} /></label>
                      <label className="field"><span className="field__label">{t('Testo')}</span>
                        <textarea rows={3} value={String(b.options.body ?? '')} onChange={(e) => blocco(i, { options: { body: e.target.value } })} /></label>
                    </>
                  )}
                </li>
              )
            })}
          </ol>
          <label className="field" style={{ maxWidth: 360 }}><span className="field__label">{t('Aggiungi un blocco')}</span>
            <select value="" onChange={(e) => { const tipo = e.target.value as EagleBloccoTipo; if (tipo) onCambia({ ...def, blocks: [...def.blocks, { type: tipo, options: {} }] }) }}>
              <option value="">—</option>
              {BLOCCHI.map((b) => <option key={b.tipo} value={b.tipo}>{b.label()}</option>)}
            </select></label>
        </fieldset>
      </div>
    </Card>
  )
}

function SceltaParametro({ ciId, chiave, cis, onCambia }: { ciId: string; chiave: string; cis: EagleMonitorato[]; onCambia: (ciId: string, key: string) => void }) {
  const [parametri, setParametri] = useState<{ key: string; name: string; kind: string }[]>([])
  useEffect(() => {
    if (!ciId) { setParametri([]); return }
    let vivo = true
    void api.ciEagle(ciId).then((d) => { if (vivo) setParametri((d.params ?? []).filter((p) => p.enabled).map((p) => ({ key: p.key, name: p.name, kind: p.kind }))) }).catch(() => { if (vivo) setParametri([]) })
    return () => { vivo = false }
  }, [ciId])
  return (
    <div className="eagle-report__riga">
      <label className="field"><span className="field__label">{t('CI')}</span>
        <select value={ciId} onChange={(e) => onCambia(e.target.value, '')}>
          <option value="">—</option>
          {cis.map((c) => <option key={c.ci_id} value={c.ci_id}>{c.name}</option>)}
        </select></label>
      <label className="field"><span className="field__label">{t('Parametro')}</span>
        <select value={chiave} onChange={(e) => onCambia(ciId, e.target.value)} disabled={!ciId}>
          <option value="">—</option>
          {parametri.map((p) => <option key={p.key} value={p.key}>{p.name || p.key} ({p.kind})</option>)}
        </select></label>
    </div>
  )
}

// ------------------------------------------------------------------- vista

function durata(s: number): string {
  if (s < 60) return t('{n} s', { n: s })
  if (s < 3600) return t('{n} min', { n: Math.round(s / 60) })
  return t('{h} h {m} min', { h: Math.floor(s / 3600), m: Math.round((s % 3600) / 60) })
}

function Vista({ esito }: { esito: EagleReportEsito }) {
  return (
    <div className="eagle-report">
      <header className="eagle-report__testata">
        <h2 style={{ margin: 0 }}>{esito.name}</h2>
        {esito.description && <p className="muted" style={{ margin: '4px 0 0' }}>{esito.description}</p>}
        <p className="muted eagle-piccolo" style={{ margin: '4px 0 0' }}>
          {t('Periodo: {da} – {a} · {n} CI', { da: formatDateTime(esito.from), a: formatDateTime(esito.to), n: esito.cis })}
        </p>
      </header>
      {esito.blocks.map((b, i) => <Blocco key={i} b={b} />)}
    </div>
  )
}

function Blocco({ b }: { b: EagleReportEsito['blocks'][number] }) {
  const d = b.data as Record<string, any>
  const n = (v: number | null | undefined) => (v == null ? '—' : `${v.toLocaleString(localeCorrente(), { maximumFractionDigits: 3 })} %`)
  switch (b.type) {
    case 'summary':
      return (
        <div className="eagle-testata" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
          <div className="stat"><div className="stat__value">{d.cis}</div><div className="stat__label">{t('CI nel report')}</div></div>
          <div className={`stat ${d.sla_missed > 0 ? 'stat--warn' : 'stat--ok'}`}><div className="stat__value">{n(d.availability_pct)}</div><div className="stat__label">{t('disponibilità media')}</div></div>
          <div className={`stat ${d.incidents > 0 ? 'stat--warn' : ''}`}><div className="stat__value">{d.incidents}</div><div className="stat__label">{t('incidenti')}</div><div className="stat__hint">{t('tempo giù {d}', { d: durata(d.down_s) })}</div></div>
          <div className={`stat ${d.sla_missed > 0 ? 'stat--error' : 'stat--ok'}`}><div className="stat__value">{d.sla_met} / {d.sla_met + d.sla_missed}</div><div className="stat__label">{t('SLA rispettati')}</div></div>
          {d.worst && <div className="stat"><div className="stat__value" style={{ fontSize: 18 }}><Link to={`/cis/${d.worst.ci_id}#eagle`}>{d.worst.ci_name}</Link></div><div className="stat__label">{t('il peggiore: {p}', { p: n(d.worst.availability_pct) })}</div></div>}
        </div>
      )
    case 'availability':
      return (
        <Card title={t('Disponibilità per CI')}>
          <DataTable<Record<string, any>> rows={d.rows} rowKey={(r) => r.ci_id} columns={[
            { key: 'ci', header: t('CI'), value: (r) => r.ci_name, render: (r) => <><Link to={`/cis/${r.ci_id}#eagle`}>{r.ci_name}</Link>{r.ci_class && <span className="muted"> · {r.ci_class}</span>}</> },
            { key: 'av', header: t('Disponibilità'), value: (r) => r.availability_pct ?? -1, render: (r) => <span className={`mono ${r.sla_met === false ? 'eagle-motivo' : ''}`}>{n(r.availability_pct)}</span> },
            { key: 'down', header: t('Giù'), value: (r) => r.down_s, render: (r) => durata(r.down_s) },
            { key: 'inc', header: t('Incidenti'), value: (r) => r.incidents },
            { key: 'cov', header: t('Copertura'), value: (r) => r.coverage_pct, render: (r) => <span className={r.coverage_pct < 90 ? 'eagle-motivo' : ''}>{r.coverage_pct} %</span> },
            { key: 'sla', header: t('SLA'), value: (r) => r.sla_name ?? '', render: (r) => r.sla_name ? <>{r.sla_name} <span className="muted">({r.sla_target} %)</span> {r.sla_met === true ? <span className="badge badge--ok">{t('rispettato')}</span> : r.sla_met === false ? <span className="badge badge--error">{t('mancato')}</span> : null}</> : <span className="muted">—</span> },
          ]} />
          <p className="muted eagle-piccolo" style={{ marginBottom: 0 }}>{t('La copertura dice quanta parte del periodo era davvero osservata: una disponibilità su poche misure vale poco.')}</p>
        </Card>
      )
    case 'incidents':
      return (
        <Card title={t('Incidenti ({n})', { n: d.rows.length })}>
          {d.rows.length === 0 ? <p className="muted" style={{ margin: 0 }}>{t('Nessun incidente nel periodo.')}</p> : (
            <DataTable<Record<string, any>> rows={d.rows} rowKey={(r, i) => `${r.ci_id}|${r.from}|${i}`} columns={[
              { key: 'ci', header: t('CI'), value: (r) => r.ci_name, render: (r) => <Link to={`/cis/${r.ci_id}#eagle`}>{r.ci_name}</Link> },
              { key: 'key', header: t('Parametro'), value: (r) => r.key, mono: true },
              { key: 'from', header: t('Da'), value: (r) => r.from, render: (r) => formatDateTime(r.from) },
              { key: 'to', header: t('A'), value: (r) => r.to, render: (r) => formatDateTime(r.to) },
              { key: 'dur', header: t('Durata'), value: (r) => r.duration_s, render: (r) => durata(r.duration_s) },
            ]} />
          )}
        </Card>
      )
    case 'alarms_timeline':
      return <Card title={t('Parametri in allarme nel tempo')}><GraficoStati punti={d.timeline} passoS={d.step_s} ore={d.hours} /></Card>
    case 'top_params':
      return (
        <Card title={t('Parametri più alti ({tipo})', { tipo: TIPI_TOP.find((x) => x.key === b.options.kind)?.label() ?? String(b.options.kind) })}>
          {d.rows.length === 0 ? <p className="muted" style={{ margin: 0 }}>{t('Nessuna misura nel periodo.')}</p> : (
            <DataTable<Record<string, any>> rows={d.rows} rowKey={(r) => `${r.ci_id}|${r.key}`} columns={[
              { key: 'ci', header: t('CI'), value: (r) => r.ci_name, render: (r) => <Link to={`/cis/${r.ci_id}#eagle`}>{r.ci_name}</Link> },
              { key: 'p', header: t('Parametro'), value: (r) => r.name || r.key },
              { key: 'avg', header: t('Media'), value: (r) => r.avg, render: (r) => <span className="mono">{formatValore(r.avg)}{r.unit ? ` ${r.unit}` : ''}</span> },
              { key: 'max', header: t('Massimo'), value: (r) => r.max, render: (r) => <span className="mono">{formatValore(r.max)}{r.unit ? ` ${r.unit}` : ''}</span> },
              { key: 'n', header: t('Misure'), value: (r) => r.samples },
            ]} />
          )}
        </Card>
      )
    case 'param_chart':
      if (d.error || !d.param) return <Card title={t('Grafico di un parametro')}><p className="muted" style={{ margin: 0 }}>{t('CI o parametro non trovato.')}</p></Card>
      return (
        <Card title={`${d.ci_name} · ${d.param.name || d.key}`}>
          <GraficoSerie punti={d.points} param={d.param} ore={d.hours} />
        </Card>
      )
    case 'sla':
      return (
        <Card title={t('Rispetto degli SLA')}>
          {d.rows.length === 0 ? <p className="muted" style={{ margin: 0 }}>{t('Nessun CI del report ha uno SLA.')}</p> : (
            <DataTable<Record<string, any>> rows={d.rows} rowKey={(r) => r.sla_id} columns={[
              { key: 'sla', header: t('SLA'), value: (r) => r.name, render: (r) => <>{r.name} <span className="muted">({r.target} %)</span></> },
              { key: 'cis', header: t('CI'), value: (r) => r.cis },
              { key: 'met', header: t('Rispettati'), value: (r) => r.met, render: (r) => <span className="badge badge--ok">{r.met}</span> },
              { key: 'missed', header: t('Mancati'), value: (r) => r.missed, render: (r) => <span className={`badge badge--${r.missed > 0 ? 'error' : 'muted'}`}>{r.missed}</span> },
              { key: 'unk', header: t('Senza dati'), value: (r) => r.unknown },
              { key: 'worst', header: t('Peggiore'), value: (r) => r.worst?.ci_name ?? '', render: (r) => r.worst ? `${r.worst.ci_name} (${n(r.worst.availability_pct)})` : '—' },
            ]} />
          )}
        </Card>
      )
    case 'text':
      return (
        <Card title={d.title || undefined}>
          {String(d.body || '').split('\n').map((riga: string, i: number) => <p key={i} style={{ margin: '0 0 6px' }}>{riga}</p>)}
        </Card>
      )
    default:
      return <Card><StatoBadge stato="nodata" /> {b.type}</Card>
  }
}

export type { EagleReportEsito }
